import React, { useCallback, useEffect, useState } from 'react';
import { useHistory } from 'react-router-dom';
import {
  Card,
  Table,
  Button,
  TableRow,
  TableBody,
  TableCell,
  TableHead,
  LinearProgress,
  TableContainer,
  TableSortLabel
} from '@material-ui/core';
import {
  Assignment as AssignmentIcon,
  Close as CloseIcon
} from '@material-ui/icons';
import CardTitle from '../../../common/card/CardTitle';
import WrappedTableCell from '../../../common/WrappedTableCell';
import TaskStatusLabel from '../../../common/TaskStatusLabel';
import { ApiParams } from '../../../common/api/api';
import ConfirmDialog from '../../../common/dialogs/ConfirmDialog';
import { BaseProductUserNeedsCardProps } from '../Product.interface';
import {
  useAssignUserNeedHazard,
  useFindHazardsByUserNeedId
} from '../Product.hooks';
import UserNeedAssignHazardDialog from '../dialogs/UserNeedAssignHazardDialog';
import { useTaskEntityFields } from '../../workflow/Workflow.hooks';
import { Hazard } from '../../hazards/Hazard.interface';
import { getHazardIdPrefix } from '../../hazards/Hazard.config';

const UserNeedHazardReferenceCard: React.FC<BaseProductUserNeedsCardProps> = (
  props: BaseProductUserNeedsCardProps
) => {
  const {
    productId,
    productUserNeed,
    productUserNeedVersion,
    onUpdate,
    onError
  } = props;
  const history = useHistory();
  const [openDialog, setOpenDialog] = useState(false);
  const [unassign, setUnassign] = useState<Hazard | undefined>();
  const [order, setOrder] = useState<string | undefined>();
  const [orderDirection, setOrderDirection] = useState<
    'ASC' | 'DESC' | undefined
  >();
  const { hazardList, isLoadingHazards, loadingHazardsError, findHazards } =
    useFindHazardsByUserNeedId(productId, productUserNeed.id);
  const {
    updatedProductUserNeed,
    error: assignError,
    assignHazards
  } = useAssignUserNeedHazard(productId, productUserNeed.id);
  const { isFieldDisabled } = useTaskEntityFields(productUserNeed.task);
  const handleSort = (column: string) => {
    setOrder(column);
    setOrderDirection(orderDirection === 'ASC' ? 'DESC' : 'ASC');
  };

  const getSortDirection = (column: string): 'asc' | 'desc' => {
    return order === column && orderDirection === 'DESC' ? 'desc' : 'asc';
  };

  const findHazardsWithParams = useCallback(() => {
    const params: ApiParams = {};
    if (order) params.order = order;
    if (orderDirection) params.direction = orderDirection;
    if (productUserNeedVersion) params.version = productUserNeedVersion.version;
    findHazards(params);
  }, [
    findHazards,
    order,
    orderDirection,
    productUserNeedVersion,
    updatedProductUserNeed
  ]);

  useEffect(() => {
    findHazardsWithParams();
  }, [findHazardsWithParams]);

  useEffect(() => {
    if (loadingHazardsError) {
      onError?.(loadingHazardsError);
    } else if (assignError) {
      onError?.(assignError);
    }
  }, [loadingHazardsError, assignError]);

  const disabled =
    Boolean(productUserNeedVersion) || isFieldDisabled('assignHazard');

  return (
    <Card>
      <CardTitle>
        <AssignmentIcon />
        Hazard Reference
      </CardTitle>
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>
                <TableSortLabel
                  active={order === 'hazard.id'}
                  direction={getSortDirection('hazard.id')}
                  onClick={() => handleSort('hazard.id')}
                >
                  ID
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'hazard.title'}
                  direction={getSortDirection('hazard.title')}
                  onClick={() => handleSort('hazard.title')}
                >
                  Title
                </TableSortLabel>
              </TableCell>
              <TableCell>
                <TableSortLabel
                  active={order === 'task.status'}
                  direction={getSortDirection('task.status')}
                  onClick={() => handleSort('task.status')}
                >
                  Status
                </TableSortLabel>
              </TableCell>
              <TableCell>Action</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoadingHazards && (
              <TableRow>
                <TableCell colSpan={2} padding='none'>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            )}
            {hazardList?.map((hazard: Hazard) => (
              <TableRow
                hover
                key={hazard.id}
                onClick={() => {
                  history.push(
                    `/portal/products/${productId}/hazards/${hazard.id}/detail`
                  );
                }}
              >
                <TableCell>{`${getHazardIdPrefix()}-${hazard.id}`}</TableCell>
                <WrappedTableCell>{hazard.title}</WrappedTableCell>
                <TableCell>
                  <TaskStatusLabel task={hazard.task} />
                </TableCell>
                <TableCell
                  style={{
                    textAlign: 'center'
                  }}
                >
                  {!disabled && (
                    <CloseIcon
                      onClick={event => {
                        event.stopPropagation();
                        setUnassign(hazard);
                      }}
                    />
                  )}
                </TableCell>
              </TableRow>
            ))}
            <TableRow>
              <TableCell colSpan={2}>
                <Button
                  variant='outlined'
                  color='primary'
                  disabled={disabled}
                  onClick={() => setOpenDialog(true)}
                >
                  Assign Hazards
                </Button>
              </TableCell>
            </TableRow>
          </TableBody>
        </Table>
      </TableContainer>
      {openDialog && (
        <UserNeedAssignHazardDialog
          open={openDialog}
          productId={productId}
          userNeedId={productUserNeed.id}
          selectedItems={hazardList}
          onError={onError}
          onCancel={() => setOpenDialog(false)}
          onAssigned={() => {
            findHazardsWithParams();
            onUpdate?.();
            setOpenDialog(false);
          }}
        />
      )}
      {unassign && (
        <ConfirmDialog
          open
          title='Are you sure?'
          message={`Are you sure you want to un-assign "${unassign.title}"?`}
          onCancel={() => setUnassign(undefined)}
          onConfirm={() => {
            if (!hazardList) return;
            const updatedReferenceIds = hazardList
              .map(hazard => hazard.id)
              .filter(id => id !== unassign.id);
            setUnassign(undefined);
            assignHazards({ hazardIds: updatedReferenceIds });
          }}
        />
      )}
    </Card>
  );
};

export default UserNeedHazardReferenceCard;
